import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { AxiosError } from 'axios'
import {
  getAttendanceToday,
  clockIn,
  startBreak,
  endBreak,
  clockOut,
  endDay,
  getAttendanceById,
  getAttendanceWorkspace,
  getMyAttendanceToday,
  updateAttendance,
  type UpdateAttendanceBody,
} from '@/api/services/attendance.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import {
  getAttendanceErrorMessage,
  getAttendanceErrorMessageWithData,
  ATTENDANCE_ERROR_CODES,
} from '@/features/attendance/utils/attendanceErrors'
import { playClockInSound, playClockOutSound } from '@/lib/sounds'
import type {
  MyAttendanceFilterQueryDto,
  ClockInDto,
  BreakStartDto,
} from '@/features/attendance/types/attendance.types'
import type { AttendanceErrorBody } from '@/features/attendance/types/attendance.types'

export const attendanceKeys = {
  all: (companyId: string | null) => ['attendance', companyId] as const,
  today: (companyId: string | null) => ['attendance', companyId, 'today'] as const,
  detail: (companyId: string | null, id: string) =>
    ['attendance', companyId, 'detail', id] as const,
  workspace: (params: MyAttendanceFilterQueryDto | undefined) =>
    ['attendance', 'workspace', params] as const,
  myToday: () => ['attendance', 'me', 'today'] as const,
}

/**
 * Clock/break events change live presence shown outside this feature:
 * the org dashboard counters (present/late/clocked-in), the wallboard,
 * and the self-service attendance history (['attendance-me']).
 */
function invalidateLivePresenceViews(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.invalidateQueries({ queryKey: ['dashboard', 'organization'] })
  queryClient.invalidateQueries({ queryKey: ['wallboard'] })
  queryClient.invalidateQueries({ queryKey: ['attendance-me'] })
  // Admin cross-company attendance list — keyed ['attendance','workspace',…],
  // which attendanceKeys.all(companyId) does not cover.
  queryClient.invalidateQueries({ queryKey: ['attendance', 'workspace'] })
}

export function useAttendanceWorkspace(
  params: MyAttendanceFilterQueryDto | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: attendanceKeys.workspace(params),
    queryFn: () => getAttendanceWorkspace(params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

function getErrorCode(error: unknown): string | undefined {
  const err = error as AxiosError<AttendanceErrorBody>
  return err.response?.data?.code
}

function getErrorData(
  error: unknown,
): AttendanceErrorBody['data'] | undefined {
  const err = error as AxiosError<AttendanceErrorBody>
  return err.response?.data?.data
}

export function useAttendanceToday(companyId: string | null) {
  return useQuery({
    queryKey: attendanceKeys.today(companyId),
    queryFn: () => getAttendanceToday(companyId!),
    enabled: !!companyId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useMyAttendanceToday(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: attendanceKeys.myToday(),
    queryFn: () => getMyAttendanceToday(),
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // The API reconciles expired shifts and the server scheduler is the
    // authority. Polling keeps the visible active-shift state in sync after
    // an automatic clock-out without requiring a manual reload.
    refetchInterval: 15 * 1000,
    refetchIntervalInBackground: true,
  })
}

export function useAttendanceById(
  companyId: string | null,
  id: string | null
) {
  return useQuery({
    queryKey: attendanceKeys.detail(companyId, id ?? ''),
    queryFn: () => getAttendanceById(companyId!, id!),
    enabled: !!companyId && !!id,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useUpdateAttendance() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { companyId: string; id: string; body: UpdateAttendanceBody }) =>
      updateAttendance(args.companyId, args.id, args.body),
    onSuccess: (_, { companyId }) => {
      toast.success('Attendance updated')
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all(companyId) })
      invalidateLivePresenceViews(queryClient)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useClockIn() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, payload }: { companyId: string; payload: ClockInDto }) =>
      clockIn(companyId, payload),
    onSuccess: (_, variables) => {
      playClockInSound()
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all(variables.companyId) })
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.today(variables.companyId),
      })
      queryClient.invalidateQueries({ queryKey: attendanceKeys.myToday() })
      invalidateLivePresenceViews(queryClient)
    },
    onError: (error) => {
      const code = getErrorCode(error)
      if (code === ATTENDANCE_ERROR_CODES.TIMER_ALREADY_RUNNING) {
        queryClient.invalidateQueries({ queryKey: ['attendance'] })
      }
      const message = getAttendanceErrorMessageWithData(
        code,
        getErrorData(error),
        getErrorMessage(error),
      )
      toast.error(message)
    },
  })
}

export function useBreakStart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      payload,
    }: {
      companyId: string
      payload?: BreakStartDto
    }) => startBreak(companyId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: attendanceKeys.myToday() })
      invalidateLivePresenceViews(queryClient)
    },
    onError: (error) => {
      const code = getErrorCode(error)
      const message = getAttendanceErrorMessage(code, getErrorMessage(error))
      toast.error(message)
    },
  })
}

export function useBreakEnd() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (companyId: string) => endBreak(companyId),
    onSuccess: (_, companyId) => {
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all(companyId) })
      queryClient.invalidateQueries({ queryKey: attendanceKeys.myToday() })
      invalidateLivePresenceViews(queryClient)
    },
    onError: (error) => {
      const code = getErrorCode(error)
      const message = getAttendanceErrorMessage(code, getErrorMessage(error))
      toast.error(message)
    },
  })
}

export function useClockOut() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (companyId: string) => clockOut(companyId),
    onSuccess: (_, companyId) => {
      playClockOutSound()
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all(companyId) })
      queryClient.invalidateQueries({ queryKey: attendanceKeys.myToday() })
      invalidateLivePresenceViews(queryClient)
    },
    onError: (error) => {
      const code = getErrorCode(error)
      const message = getAttendanceErrorMessage(code, getErrorMessage(error))
      toast.error(message)
    },
  })
}

export function useEndDay() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (companyId: string) => endDay(companyId),
    onSuccess: (_, companyId) => {
      playClockOutSound()
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all(companyId) })
      queryClient.invalidateQueries({ queryKey: attendanceKeys.myToday() })
      invalidateLivePresenceViews(queryClient)
      toast.success('Day ended')
    },
    onError: (error) => {
      const code = getErrorCode(error)
      const message = getAttendanceErrorMessage(code, getErrorMessage(error))
      toast.error(message)
    },
  })
}
